import { IconComponentType } from '@creditflowresearch/uilib/Icon';
import { IProcessToolsOptions, IProcessedTools } from '@/creditflowresearch/domain/vo/ITools';
import LinkBuilder from '@/creditflowresearch/domain/Header/Tool/builders/LinkBuilder';
import ToolTransformer from '@/creditflowresearch/domain/Header/Tool/transformers/ToolTransformer';
import ToolValidator, { IValidationResult } from '@/creditflowresearch/domain/Header/Tool/validators/ToolValidator';
import { ToolIconResolver, ToolNameResolver, IGetToolNameOptions, ToolClassifier } from '@/creditflowresearch/domain/Header/Tool/utils';
import { HeaderMappingItem } from '@/creditflowresearch/application/actions/profile/helpers/types';

export {
  MARKET_OVERVIEW_ALL,
  MARKET_OVERVIEW_IGC,
  MARKET_OVERVIEW_HYC,
  MARKET_OVERVIEW_CONSUMER,
  BOND_SCREENER,
  CORPORATE_BOND_SCREENER,
  CREDIT_FLOW_ARTICLES,
  BWICS,
  TRACE,
  LEAGUE_TABLE,
  LOAN_LEVEL_DATA,
  NEW_ISSUER_PIPELINE,
  OPEN_MARKET,
  PORTFOLIO_MONITOR,
  RESEARCH,
  NEW_ISSUE_STATS,
  INVESTOR_SET,
  CAPEDGE_HOME,
  CALENDAR,
  COURT,
  EARNINGS_CALENDAR,
  INSIDER,
  SPLITS,
  EARNINGS,
  INDUSTRIAL,
  CRYPTO,
  ISSUE_REGISTRATION,
  REDDIT,
  ACTIVE_CALENDAR,
  CFX_INDICES,
  CFX_CORPORATE_INDICES,
  IG_ACTIVE_CALENDAR,
  IG_PIPELINE,
  HYC_ACTIVE_CALENDAR,
  HYC_PIPELINE,
} from '@/creditflowresearch/domain/Header/Tool/constants';

export interface IGetToolLinkOptions {
  isExternal: boolean,
  id: string,
  route?: string,
  skipProductRegionAppend?: boolean | string,
  activeProductAbbreviation: string,
  activeRegionAbbreviation: string,
}

class Tool {
  static getToolIconByID(toolId: string): IconComponentType {
    return ToolIconResolver.resolve(toolId);
  }

  static getToolName(options: IGetToolNameOptions): string {
    return ToolNameResolver.resolve(options);
  }

  static getToolLink(options: IGetToolLinkOptions): string | { pathname: string, search: string } {
    if (options.isExternal) {
      const link = LinkBuilder.buildExternalLink(options.id, options.route || '');
      return link || '';
    }
    return LinkBuilder.buildInternalLink(
      options.route || '',
      options.activeProductAbbreviation,
      options.activeRegionAbbreviation,
      options.skipProductRegionAppend,
      options.id,
    );
  }

  static getExternalLink(options: { id: string, route: string }): string | null {
    return LinkBuilder.buildExternalLink(options.id, options.route);
  }

  static getInternalLink(options: {
    route: string,
    activeProductAbbreviation: string,
    activeRegionAbbreviation: string,
    skipProductRegionAppend?: boolean | string,
  }): { pathname: string, search: string } {
    return LinkBuilder.buildInternalLink(
      options.route,
      options.activeProductAbbreviation,
      options.activeRegionAbbreviation,
      options.skipProductRegionAppend,
    );
  }

  static getMarketOverviewToolIds(): string[] {
    return ToolClassifier.getMarketOverviewToolIds();
  }

  static isMarketOverviewTool(toolId: string): boolean {
    return ToolClassifier.isMarketOverviewTool(toolId);
  }

  static processToolsForPanel(options: IProcessToolsOptions): IProcessedTools {
    if (!options.tools?.length) {
      return { topOptions: [], toolsOptions: [] };
    }

    const { marketOverviewTools, regularTools } = ToolTransformer.categorize(options.tools);

    const context = {
      activeProductAbbreviation: options.activeProductAbbreviation,
      activeRegionAbbreviation: options.activeRegionAbbreviation,
      productAbbreviation: options.productAbbreviation,
      productId: options.productId,
    };

    const topOptions = marketOverviewTools.map((tool) =>
      ToolTransformer.transformToLinkOption(tool, context),
    );

    const toolsOptions = regularTools.map((tool) =>
      ToolTransformer.transformToLinkOption(tool, context),
    );

    return { topOptions, toolsOptions };
  }

  static validateToolData(
    mapping: Record<string, HeaderMappingItem>,
    panelId: string,
    isHeaderInitialized: boolean,
    regionId: string,
  ): IValidationResult {
    return ToolValidator.validate(mapping, panelId, isHeaderInitialized, regionId);
  }
}

export default Tool;
